import * as React from 'react';
import { cn } from '@/utils/cn';
import { Spinner } from '../../skeleton/spinner';

const variants = {
  default:
    'bg-gray-100 text-gray-700 hover:bg-gray-200 focus-visible:ring-gray-300 disabled:bg-gray-200 disabled:text-gray-400',
  primary:
    'bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-blue-600 disabled:bg-blue-400 disabled:text-white',
  outline:
    'border border-gray-300 text-gray-700 hover:bg-gray-100 focus-visible:ring-gray-300 disabled:text-gray-400 disabled:border-gray-200',
  ghost:
    'text-gray-600 hover:bg-gray-100 focus-visible:ring-gray-300 disabled:text-gray-400 disabled:hover:bg-transparent',
  destructive:
    'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-600 disabled:bg-red-400 disabled:text-white',
  loading: 'bg-gray-200 text-gray-500 disabled:bg-gray-200 disabled:text-gray-500',
};

const sizes = {
  sm: 'p-1.5',
  md: 'p-2',
  lg: 'p-3',
};

export interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof variants;
  size?: keyof typeof sizes;
  isRounded?: boolean;
  isLoading?: boolean; // 👈 new
  loadingIconClassName?: string;
}

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      className,
      variant = 'default',
      size = 'md',
      isRounded = true,
      disabled,
      isLoading = false,
      children,
      loadingIconClassName,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || isLoading;

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        onClick={(e) => {
          if (isDisabled) return;
          props?.onClick?.(e);
        }}
        className={cn(
          'inline-flex items-center justify-center transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2  disabled:cursor-not-allowed',
          variants[isLoading ? 'loading' : variant],
          sizes[size],
          isRounded ? 'rounded-full' : 'rounded-lg',
          className
        )}
        {...props}
      >
        {isLoading ? <Spinner isLoading={isLoading} className={cn(loadingIconClassName, 'border-[2px]')} /> : children}
      </button>
    );
  }
);

IconButton.displayName = 'IconButton';

export default IconButton;
